import React, { useCallback, useMemo, useState } from 'react'
import bankDetails from '../payment/bank-details'
import { copyTextToClipboard } from '../../lib/utils/copyToClipboard'
import { OpportunityDetails, Investment } from '../../lib/types'
import './invest-v2-bank-cards.scss'

const BankIcon = () => (
  <svg
    className='iv2-bank-card__icon'
    width='22'
    height='22'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
  >
    <path
      d='M3 10.5L12 4l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1v-9.5z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinejoin='round'
    />
    <path d='M9 21V12h6v9' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
)

const CopyIcon = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden>
    <path
      d='M8 7V5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M8 7h8a2 2 0 012 2v2'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

const USD_WIRE_FIELD_ORDER = [
  'beneficiaryName',
  'cif',
  'iban',
  'accountNumber',
  'beneficiaryBank',
  'bankAddress',
  'swiftCode',
  'intermediaryBank',
  'intermediaryAccountNumber',
  'intermediarySwiftCode',
] as const

const PURPOSE_LABEL = 'PURPOSE OF TRANSACTION:'
const PURPOSE_VALUE = 'Investment through Credibila Markets'

type Props = {
  opportunity?: Pick<OpportunityDetails, 'projectName'>
  investment?: Pick<Investment, 'id' | 'order_id'>
  referenceCode?: string
  purposeValue?: string
  /** Show bank-transfer assistance note under Purpose of Transaction (Add Funds) */
  showAssistanceNote?: boolean
}

const InvestV2WireTransferCard = ({
  opportunity,
  investment,
  referenceCode: referenceCodeProp,
  purposeValue: purposeValueProp,
  showAssistanceNote = false,
}: Props) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const wd = bankDetails.wireTransferDetails
  const purposeValue = purposeValueProp || PURPOSE_VALUE

  const referenceCode = useMemo(() => {
    if (referenceCodeProp) return referenceCodeProp
    const oid = investment?.order_id?.trim()
    if (oid) return oid
    if (investment?.id) return `INV-${investment.id}`
    return 'WALLET-TOPUP'
  }, [investment, referenceCodeProp])

  const copyAllText = useMemo(() => {
    const lines = [
      'DETAILS: USD',
      ...USD_WIRE_FIELD_ORDER.map((key) => {
        const item = wd[key]
        return `${item.title} ${item.usdValue}`
      }),
      `${PURPOSE_LABEL} ${purposeValue}`,
      `Reference (bank memo): ${referenceCode}`,
    ]
    if (opportunity?.projectName) {
      return [`Opportunity: ${opportunity.projectName}`, ...lines].join('\n')
    }
    return lines.join('\n')
  }, [opportunity?.projectName, purposeValue, referenceCode, wd])

  const flashCopied = useCallback((key: string) => {
    setCopiedKey(key)
    window.setTimeout(() => setCopiedKey(null), 1600)
  }, [])

  const onCopy = useCallback(
    async (key: string, text: string) => {
      const ok = await copyTextToClipboard(text)
      if (ok) flashCopied(key)
    },
    [flashCopied],
  )

  return (
    <div className='iv2-bank-card' data-testid='invest-v2-wire-transfer-card'>
      <div className='iv2-bank-card__head'>
        <div className='iv2-bank-card__head-left'>
          <BankIcon />
          <h3 className='iv2-bank-card__title'>Wire transfer details</h3>
        </div>
        <button
          type='button'
          className='iv2-bank-card__copy-all'
          onClick={() => void onCopy('all', copyAllText)}
        >
          <CopyIcon />
          {copiedKey === 'all' ? 'Copied' : 'Copy all'}
        </button>
      </div>
      <div className='iv2-bank-card__body'>
        {/* <div className='iv2-bank-field'>
          <span className='iv2-bank-field__label iv2-bank-field__label--wire'>DETAILS</span>
          <div className='iv2-bank-field__row'>
            <span className='iv2-bank-field__value'>USD</span>
            <button
              type='button'
              className='iv2-bank-field__copy'
              aria-label='Copy currency'
              onClick={() => void onCopy('currency', 'USD')}
            >
              <CopyIcon />
            </button>
          </div>
        </div> */}
        {USD_WIRE_FIELD_ORDER.map((key) => {
          const item = wd[key]
          const value = item.usdValue
          const labelPlain = item.title.replace(/:\s*$/, '')
          return (
            <div key={key} className='iv2-bank-field'>
              <span className='iv2-bank-field__label iv2-bank-field__label--wire'>
                {item.title}
              </span>
              <div className='iv2-bank-field__row'>
                <span className='iv2-bank-field__value'>{value}</span>
                <button
                  type='button'
                  className='iv2-bank-field__copy'
                  aria-label={`Copy ${labelPlain}`}
                  onClick={() => void onCopy(key, value)}
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          )
        })}
        <div className='iv2-bank-field iv2-bank-field--purpose'>
          <div className='iv2-bank-purpose-panel'>
            <span className='iv2-bank-field__label iv2-bank-field__label--purpose'>
              {PURPOSE_LABEL}
            </span>
            <div className='iv2-bank-field__row'>
              <span className='iv2-bank-purpose-value'>{purposeValue}</span>
              <button
                type='button'
                className='iv2-bank-field__copy iv2-bank-field__copy--on-dark'
                aria-label='Copy purpose of transaction'
                onClick={() => void onCopy('purpose', purposeValue)}
              >
                <CopyIcon />
              </button>
            </div>
          </div>
          {showAssistanceNote ? (
            <p className='iv2-bank-purpose-note' data-testid='wire-purpose-assistance-note'>
              Please note that if you are unable to provide the above-mentioned Purpose of
              Transaction through your bank&apos;s platform at the time of transfer, please contact
              us at <a href='mailto:support@credibila.com'>support@credibila.com</a> for assistance.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export { InvestV2WireTransferCard }
